import React, { useEffect, useState } from 'react';
import { Download, FileText, Loader2, Maximize2, Minimize2 } from 'lucide-react';
import Modal from '../../components/Modal';
import { apiFetch, apiUrl } from '../../services/apiClient';
import { previewFilePath, previewFileStatus } from './contractPreviewUtils';

function PreviewDocument({ contractId, file }) {
  const [result, setResult] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const path = previewFilePath(contractId, file);
  const isWord = /\.docx?$/i.test(file.name);

  useEffect(() => {
    if (isWord) return undefined;
    const controller = new AbortController();
    let objectUrl;
    setResult(null);
    async function load() {
      try {
        const response = await apiFetch(path, { signal: controller.signal });
        if (!response.ok) {
          const message = response.status === 401 ? 'Your session has expired. Sign in again to view this agreement.'
            : response.status === 403 || response.status === 404 ? 'This file is unavailable or you no longer have access to it.'
              : 'The preview could not be loaded. Try again or download the file.';
          throw new Error(message);
        }
        const blob = await response.blob();
        if (controller.signal.aborted) return;
        if (blob.type.split(';')[0] !== 'application/pdf') {
          setResult({ unsupported: true });
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setResult({ url: objectUrl });
      } catch (error) {
        if (!controller.signal.aborted) setResult({ error: error.message || 'The preview could not be loaded.' });
      }
    }
    load();
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path, isWord, attempt]);

  if (result?.url) {
    return (
      <iframe
        title={`Agreement preview: ${file.name}`}
        tabIndex={0}
        src={`${result.url}#view=FitH`}
        className="w-full flex-1 min-h-0 border-0"
        onError={() => setResult({ error: 'Your browser could not display this PDF. Download the file to read it.' })}
      />
    );
  }

  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center" aria-live="polite">
      {isWord || result?.unsupported ? (
        <>
          <FileText className="w-8 h-8 muted" />
          <div>
            <p>Preview is available for PDF files.</p>
            <p className="muted mt-2">Download this document to open it in your preferred app.</p>
          </div>
          <a className="btn outline" href={apiUrl(path)} download={file.name}><Download className="w-4 h-4" /> Download file</a>
        </>
      ) : result?.error ? (
        <>
          <p role="alert">{result.error}</p>
          <button className="btn outline" onClick={() => setAttempt((value) => value + 1)}>Try again</button>
        </>
      ) : (
        <><Loader2 className="w-6 h-6 animate-spin muted" /><p className="muted">Loading agreement…</p></>
      )}
    </div>
  );
}

export default function ContractPreviewPanel({ contractId, files, selectedKey, onSelect, onClose }) {
  const [expanded, setExpanded] = useState(false);
  const file = files.find((item) => item.key === selectedKey);
  if (!file) return null;
  const status = previewFileStatus(file);

  return (
    <Modal
      isOpen
      layout="drawer"
      title="View agreement"
      closeLabel="Close preview"
      onClose={onClose}
      contentStyle={{ overflow: 'hidden', ...(expanded ? { width: '100vw' } : {}) }}
      headerActions={(
        <div className="row" style={{ gap: 6 }}>
          <a className="icon-btn sm" href={apiUrl(previewFilePath(contractId, file))} download={file.name} title="Download file" aria-label="Download file">
            <Download className="w-4 h-4" />
          </a>
          <button className="icon-btn sm" onClick={() => setExpanded((value) => !value)} title={expanded ? 'Restore panel' : 'Expand preview'} aria-label={expanded ? 'Restore panel' : 'Expand preview'}>
            {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      )}
    >
      <div className="flex items-center gap-3 px-5 py-3" style={{ borderBottom: '1px solid var(--border-primary)' }}>
        <div className="min-w-0 flex-1">
          {files.length > 1 ? (
            <select className="select w-full" aria-label="File version" value={selectedKey} onChange={(event) => onSelect(event.target.value)}>
              {files.map((item) => <option key={item.key} value={item.key}>{previewFileStatus(item)} · {item.name}</option>)}
            </select>
          ) : <div className="truncate" title={file.name}>{file.name}</div>}
          {file.dateLabel && <div className="muted text-xs mt-1">{file.dateLabel}</div>}
        </div>
        <span className={`chip sq ${file.fileKind === 'reference' ? 'purple' : file.signed ? 'green' : 'gray'}`}>{status}</span>
      </div>
      <PreviewDocument key={`${contractId}:${file.key}`} contractId={contractId} file={file} />
    </Modal>
  );
}
